import { CardsStackedProps } from 'libs/contentful/props';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { cn, getSectionProps } from 'utils/styles';
import { CardLayout } from 'types/constants';
import { CardsStackedVariants } from './constants';
import CardsGridComplexItem from 'components/CardsStacked/CardsStackedItemImageVariant';
import CardsStackedItemImageV2Variant from 'components/CardsStacked/CardsStackedItemImageV2Variant';
import CardsGridSimpleItem from 'components/CardsStacked/CardsStackedItemCopyVariant';
import CardsStackedItemIconVariant from 'components/CardsStacked/CardsStackedItemIconVariant';
import Disclosure from 'components/DisclosureGroupings/Disclosure';

const CardsStacked = ({
  sys,
  variant,
  sectionName,
  cards,
  color,
  margins,
  cardLayout,
  disclosure,
}: CardsStackedProps) => {
  const items = cards?.items;
  const numItems = items?.length;
  const dataAttr = { 'data-num-items': numItems };
  const reverseCardLayout = cardLayout === CardLayout.TwoOnTop && variant !== CardsStackedVariants.ImageV2;

  // Classes necessary if the variant supports card layout options
  const cardLayoutClasses = cn('flex flex-row gap-6 group', reverseCardLayout ? 'flex-wrap-reverse' : 'flex-wrap');

  const renderCardItems = () => {
    switch (variant) {
      case CardsStackedVariants.Copy:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-12 md:py-20" {...dataAttr}>
            {items?.map(item => (
              <CardsGridSimpleItem
                key={item.sys?.id}
                sectionName={item.sectionName}
                eyebrowRte={item.eyebrow}
                headline={item.headline}
                color={item.color}
                iconAsset={item.iconAsset}
                chimeKitIllustrationToken={item.chimeKitIllustrationToken}
                shortDescription={item.shortDescription}
                cta={item.cta}
              />
            ))}
          </div>
        );
      case CardsStackedVariants.Image:
        return (
          <div className={cardLayoutClasses} {...dataAttr}>
            {items?.map(item => (
              <CardsGridComplexItem
                key={item.sys?.id}
                sectionName={item.sectionName}
                desktopMedia={item.desktopMedia}
                desktopAspectRatio={item.desktopAspectRatio}
                mobileMedia={item.mobileMedia}
                mobileAspectRatio={item.mobileAspectRatio}
                eyebrowRte={item.eyebrow}
                headline={item.headline}
                shortDescription={item.shortDescription}
                cta={item.cta}
                color={item.color}
                topRightBadge={item.topRightBadge}
                desktopAnimation={item.desktopAnimation}
                mobileAnimation={item.mobileAnimation}
              />
            ))}
          </div>
        );
      case CardsStackedVariants.ImageV2:
        return (
          <div className={cardLayoutClasses} {...dataAttr}>
            {items?.map(item => (
              <CardsStackedItemImageV2Variant
                key={item.sys?.id}
                sys={item.sys}
                desktopMedia={item.desktopMedia}
                mobileMedia={item.mobileMedia}
                eyebrowRte={item.eyebrow}
                headline={item.headline}
                shortDescription={item.shortDescription}
                cta={item.cta}
                color={item.color}
                topRightBadge={item.topRightBadge}
                badgeOverlayAlignment={item.badgeOverlayAlignment}
                copyAlignment={item.copyAlignment}
                bulletStyle={item.bulletStyle}
                iconAsset={item.iconAsset}
                desktopAnimation={item.desktopAnimation}
                mobileAnimation={item.mobileAnimation}
              />
            ))}
          </div>
        );
      case CardsStackedVariants.Icon:
        return (
          <div className={cardLayoutClasses} {...dataAttr}>
            {items?.map(item => (
              <CardsStackedItemIconVariant
                key={item.sys?.id}
                sectionName={item.sectionName}
                headline={item.headline}
                color={item.color}
                desktopMedia={item.desktopMedia}
                shortDescription={item.shortDescription}
                desktopAnimation={item.desktopAnimation}
              />
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <section {...getSectionProps(color, margins, sectionName)} {...liveUpdateAttrs(sys?.id, 'cards')}>
      <div className="content-wrapper">
        {renderCardItems()}
        {disclosure && <Disclosure key={disclosure.sys?.id} {...disclosure} />}
      </div>
    </section>
  );
};

export default CardsStacked;
